import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from '../components/HelpHint';
import { AllowancesTable } from '../components/AllowancesTable';
import { PerformanceTable } from '../components/PerformanceTable';
import {
  TechnicianForm,
  emptyTechnician,
  type TechnicianDraft,
} from '../components/TechnicianForm';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { ScrollX } from '../components/ScrollX';
import { Pager } from '../components/DataTable';

export function TechniciansPage() {
  const [page, setPage] = useState(1);
  const { data, isPending } = useQuery({
    queryKey: ['technicians', page],
    queryFn: () => api.technicians({ page }),
    placeholderData: (previous) => previous,
  });
  const { can } = useSession();
  const [draft, setDraft] = useState<TechnicianDraft | null>(null);

  if (isPending) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;

  const technicians = data?.technicians ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">Technicians</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {/*
              The total, not this page. "3 on shift of 4" while the roster holds
              forty would be a lie the moment the roster outgrew one page — and
              the count under a heading is exactly the number somebody trusts
              without checking.
            */}
            {technicians.filter((t) => t.isActive).length} on shift · {data?.total ?? 0} on the
            roster
          </p>
        </div>
        {can('technician:write') && !draft && (
          <button type="button" className="btn-primary" onClick={() => setDraft(emptyTechnician())}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add technician
          </button>
        )}
      </div>

      {draft && (
        <TechnicianForm
          draft={draft}
          onChange={setDraft}
          onDone={() => setDraft(null)}
          onCancel={() => setDraft(null)}
        />
      )}

      <PerformanceTable />

      {/* Pay, so it follows the same permission as the roster itself. */}
      {can('technician:read') && <AllowancesTable />}

      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        <ScrollX>
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    Number
                    <HelpHint>
                      Where their tickets are sent. Changing it is an admin action — a stale number
                      is the commonest reason a job goes unanswered.
                    </HelpHint>
                  </span>
                </th>
                <th className="px-4 py-3 font-semibold">Channels</th>
                <th className="px-4 py-3 font-semibold">Zones</th>
                <th className="px-4 py-3 font-semibold">Load</th>
                <th className="px-4 py-3 font-semibold">Skills</th>
                {can('technician:write') && <th className="px-4 py-3"><span className="sr-only">Edit</span></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {technicians.map((tech) => (
                <tr key={tech.id} className={tech.isActive ? '' : 'opacity-55'}>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="font-medium">{tech.name}</span>
                    <div className="mt-0.5 flex flex-wrap gap-1.5">
                      {!tech.isActive && (
                        <span className="badge bg-muted text-muted-foreground">off shift</span>
                      )}
                      {tech.certExpired && (
                        <span className="badge bg-danger-50 text-danger-600">
                          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                          pole cert expired
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {tech.phoneE164}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {tech.channels.map((channel, index) => (
                        <span
                          key={channel}
                          className={`badge ${
                            index === 0
                              ? 'bg-primary-50 text-primary-700'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {channel}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {tech.zones.join(', ') || '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span
                      className={
                        tech.openJobs >= tech.maxConcurrent ? 'font-semibold text-accent-600' : ''
                      }
                    >
                      {tech.openJobs}/{tech.maxConcurrent}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {tech.skills.join(', ')}
                  </td>
                  {can('technician:write') && (
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        className="btn-secondary px-2 py-1 text-xs"
                        onClick={() =>
                          setDraft({
                            id: tech.id,
                            name: tech.name,
                            phone: tech.phoneE164,
                            email: tech.email ?? '',
                            altPhone: '',
                            channels: tech.channels,
                            zones: tech.zones,
                            skills: tech.skills,
                            maxConcurrent: tech.maxConcurrent,
                            certPoleExpiry: tech.certPoleExpiry
                              ? String(tech.certPoleExpiry).slice(0, 10)
                              : '',
                            employedSince: tech.employedSince
                              ? String(tech.employedSince).slice(0, 10)
                              : '',
                            isActive: tech.isActive,
                          })
                        }
                      >
                        <Pencil className="h-3 w-3" aria-hidden="true" />
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      </div>

      {data && data.pages > 1 && (
        <Pager
          page={{ page: data.page, perPage: data.perPage, total: data.total, pages: data.pages }}
          onPage={setPage}
        />
      )}
    </div>
  );
}
